/* Mantenimiento de temas de Capacitaciones, por tipo (Técnica / Seguridad).
 *
 * Solo para los admins del tipo (AE_C_Admin). Un tema no se borra: se da de
 * baja y deja de ofrecerse para registrar (lo ya registrado no cambia). Las
 * preguntas de la evaluación se editan en CapacitacionTemaEvaluacion.
 */
import { IonButton, IonContent, IonIcon, IonPage, useIonToast } from "@ionic/react";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
} from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { addOutline, createOutline, helpCircleOutline, schoolOutline } from "ionicons/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Redirect, useHistory, useParams } from "react-router-dom";
import Header from "src/components/Header";
import RegistroCapacitacionAPI, {
  RolCapacitacion,
  TemaAdmin,
  TemaPayload,
  TipoCapacitacion,
  etiquetaTipo,
} from "src/services/registroCapacitacion";
import "./CapacitacionAdm.css";

// Sugerencias; se puede escribir otro valor.
const PARTICIPANTES = [
  "Todo el personal activo",
  "Administrativos",
  "Operarios",
  "Operarios nuevos",
  "Supervisores",
  "Supervisores Nuevos",
  "Operarios y Supervisores",
];
const FRECUENCIAS = ["Todos los meses", "Trimestral", "Semestral", "Anual", "Al ingreso", "Según necesidad"];

const TEMA_VACIO: TemaPayload = { Categoria: "", Tema: "", Horas: "1", Participantes: "", Frecuencia: "" };

export const tipoDesdeRuta = (valor?: string): TipoCapacitacion | null => {
  const t = String(valor || "").toUpperCase();
  return t === "TECNICA" || t === "SEGURIDAD" ? t : null;
};

/** Saca de la ruta a quien no administra el tipo (rol null = todavía cargando). */
export const GuardMantenimientoCapacitacion: React.FC<{ rol: RolCapacitacion | null }> = ({ rol }) => {
  const { tipo } = useParams<{ tipo: string }>();
  const t = tipoDesdeRuta(tipo);
  if (rol && (!t || !rol.tipos_admin.includes(t))) return <Redirect to="/" />;
  return null;
};

const CampoLibre: React.FC<{
  label: string;
  opciones: string[];
  valor: string;
  onChange: (v: string) => void;
  maxLength: number;
}> = ({ label, opciones, valor, onChange, maxLength }) => (
  <MuiAutocomplete
    freeSolo
    options={opciones}
    inputValue={valor}
    onInputChange={(_, v) => onChange(v)}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        variant="outlined"
        fullWidth
        margin="dense"
        inputProps={{ ...params.inputProps, maxLength }}
      />
    )}
  />
);

const CapacitacionMantenimiento: React.FC = () => {
  const { tipo: tipoRuta } = useParams<{ tipo: string }>();
  const tipo = tipoDesdeRuta(tipoRuta);
  const history = useHistory();
  const [present] = useIonToast();

  const [temas, setTemas] = useState<TemaAdmin[]>([]);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [verInactivos, setVerInactivos] = useState(false);

  // Diálogo de alta / edición
  const [editando, setEditando] = useState<TemaAdmin | null>(null);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [form, setForm] = useState<TemaPayload>(TEMA_VACIO);
  const [guardando, setGuardando] = useState(false);

  const toastError = useCallback(
    (err: any, defecto: string) =>
      present({ message: err?.response?.data?.error || defecto, duration: 4000, color: "danger" }),
    [present]
  );

  const cargar = useCallback(async () => {
    if (!tipo) return;
    setCargando(true);
    try {
      setTemas(await RegistroCapacitacionAPI.adminTemas(tipo));
    } catch (err: any) {
      setTemas([]);
      toastError(err, "No se pudieron cargar los temas");
    } finally {
      setCargando(false);
    }
  }, [tipo, toastError]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const categorias = useMemo(
    () => Array.from(new Set(temas.map((t) => t.Categoria).filter(Boolean))).sort(),
    [temas]
  );

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return temas.filter(
      (t) =>
        (verInactivos || t.Activo) &&
        (!q || t.Tema.toLowerCase().includes(q) || t.Categoria.toLowerCase().includes(q))
    );
  }, [temas, busqueda, verInactivos]);

  const porCategoria = useMemo(() => {
    const grupos: Record<string, TemaAdmin[]> = {};
    visibles.forEach((t) => {
      (grupos[t.Categoria] = grupos[t.Categoria] || []).push(t);
    });
    return Object.entries(grupos).sort(([a], [b]) => a.localeCompare(b));
  }, [visibles]);

  const abrirNuevo = () => {
    setEditando(null);
    setForm(TEMA_VACIO);
    setDialogoAbierto(true);
  };

  const abrirEdicion = (t: TemaAdmin) => {
    setEditando(t);
    setForm({
      Categoria: t.Categoria,
      Tema: t.Tema,
      Horas: String(t.Horas ?? ""),
      Participantes: t.Participantes,
      Frecuencia: t.Frecuencia,
    });
    setDialogoAbierto(true);
  };

  const guardarTema = async () => {
    if (!tipo) return;
    if (!form.Categoria.trim() || !form.Tema.trim() || !form.Participantes.trim() || !form.Frecuencia.trim()) {
      present({ message: "Completá todos los campos", duration: 3000, color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      if (editando) {
        await RegistroCapacitacionAPI.adminActualizarTema(editando.Id, form);
      } else {
        await RegistroCapacitacionAPI.adminCrearTema({ ...form, Tipo: tipo });
      }
      present({ message: editando ? "Tema actualizado" : "Tema creado", duration: 2500, color: "success" });
      setDialogoAbierto(false);
      cargar();
    } catch (err: any) {
      toastError(err, "No se pudo guardar el tema");
    } finally {
      setGuardando(false);
    }
  };

  const cambiarEstado = async (t: TemaAdmin) => {
    try {
      const res = await RegistroCapacitacionAPI.adminEstadoTema(t.Id, !t.Activo);
      present({ message: res.data?.info || "Listo", duration: 2500, color: "success" });
      cargar();
    } catch (err: any) {
      toastError(err, "No se pudo cambiar el estado del tema");
    }
  };

  const titulo = `Mantenimiento · Capacitación ${etiquetaTipo(tipo || "")}`;

  if (!tipo) {
    return (
      <IonPage>
        <Header titulo="Mantenimiento de capacitaciones" />
        <IonContent>
          <div className="capadm-vacio">Tipo de capacitación inválido.</div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo={titulo} />
      <IonContent id="capacitacion-mantenimiento">
        <div className="capadm-page capadm-scope campos-mui">
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <TextField
              variant="outlined"
              size="small"
              placeholder="Buscar tema o categoría"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              style={{ flex: "1 1 220px" }}
            />
            <IonButton onClick={abrirNuevo}>
              <IonIcon slot="start" icon={addOutline} /> Nuevo tema
            </IonButton>
          </div>
          <FormControlLabel
            control={<Switch checked={verInactivos} onChange={(e) => setVerInactivos(e.target.checked)} color="primary" />}
            label="Mostrar temas dados de baja"
          />

          {cargando && (
            <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
              <CircularProgress />
            </div>
          )}

          {!cargando && visibles.length === 0 && (
            <div className="capadm-vacio">
              <IonIcon icon={schoolOutline} />
              {temas.length === 0 ? "Todavía no hay temas cargados." : "Ningún tema coincide con la búsqueda."}
            </div>
          )}

          {!cargando &&
            porCategoria.map(([categoria, lista]) => (
              <div key={categoria} className="capadm-card">
                <p className="capadm-card-titulo">{categoria}</p>
                {lista.map((t) => (
                  <div
                    key={t.Id}
                    style={{
                      borderTop: "1px solid #eef1f5",
                      padding: "10px 0",
                      opacity: t.Activo ? 1 : 0.6,
                    }}
                  >
                    <div style={{ fontWeight: 600, fontSize: 14 }}>{t.Tema}</div>
                    <div className="capadm-meta">
                      <span>{t.Horas} h</span>
                      <span>{t.Participantes}</span>
                      <span>{t.Frecuencia}</span>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
                      <Chip
                        size="small"
                        label={t.Preguntas === 1 ? "1 pregunta" : `${t.Preguntas} preguntas`}
                        color={t.Preguntas > 0 ? "primary" : "default"}
                        variant="outlined"
                      />
                      {t.Registros > 0 && (
                        <Chip size="small" variant="outlined" label={`${t.Registros} registrada${t.Registros !== 1 ? "s" : ""}`} />
                      )}
                      {!t.Activo && <Chip size="small" label="Dado de baja" />}
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                      <IonButton size="small" fill="clear" onClick={() => abrirEdicion(t)}>
                        <IonIcon slot="start" icon={createOutline} /> Editar
                      </IonButton>
                      <IonButton
                        size="small"
                        fill="clear"
                        onClick={() =>
                          history.push(`/capacitaciones/mantenimiento/${tipo.toLowerCase()}/tema/${t.Id}`)
                        }
                      >
                        <IonIcon slot="start" icon={helpCircleOutline} /> Evaluación
                      </IonButton>
                      <IonButton
                        size="small"
                        fill="clear"
                        color={t.Activo ? "danger" : "success"}
                        onClick={() => cambiarEstado(t)}
                      >
                        {t.Activo ? "Dar de baja" : "Activar"}
                      </IonButton>
                    </div>
                  </div>
                ))}
              </div>
            ))}
        </div>

        <Dialog open={dialogoAbierto} onClose={() => !guardando && setDialogoAbierto(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editando ? "Editar tema" : `Nuevo tema · ${etiquetaTipo(tipo)}`}</DialogTitle>
          <DialogContent>
            <CampoLibre
              label="Categoría"
              opciones={categorias}
              valor={form.Categoria}
              onChange={(v) => setForm((f) => ({ ...f, Categoria: v }))}
              maxLength={200}
            />
            <TextField
              label="Tema"
              variant="outlined"
              fullWidth
              margin="dense"
              value={form.Tema}
              inputProps={{ maxLength: 300 }}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, Tema: v }));
              }}
            />
            <TextField
              label="Horas"
              variant="outlined"
              fullWidth
              margin="dense"
              type="number"
              inputProps={{ min: 0.5, max: 99.9, step: 0.5 }}
              value={form.Horas}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, Horas: v }));
              }}
            />
            <CampoLibre
              label="Participantes"
              opciones={PARTICIPANTES}
              valor={form.Participantes}
              onChange={(v) => setForm((f) => ({ ...f, Participantes: v }))}
              maxLength={200}
            />
            <CampoLibre
              label="Frecuencia"
              opciones={FRECUENCIAS}
              valor={form.Frecuencia}
              onChange={(v) => setForm((f) => ({ ...f, Frecuencia: v }))}
              maxLength={100}
            />
            {editando && editando.Registros > 0 && (
              <div className="capadm-aviso info" style={{ marginTop: 8, marginBottom: 0 }}>
                Las {editando.Registros} capacitaciones ya registradas conservan el nombre con el que se cargaron.
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <IonButton fill="clear" onClick={() => setDialogoAbierto(false)} disabled={guardando}>
              Cancelar
            </IonButton>
            <IonButton onClick={guardarTema} disabled={guardando}>
              {guardando ? "Guardando..." : "Guardar"}
            </IonButton>
          </DialogActions>
        </Dialog>
      </IonContent>
    </IonPage>
  );
};

export default CapacitacionMantenimiento;
